import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { UitkTertiaryButton } from "uitk-react-button";
import { UitkText } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";

import { useLocalization } from "@shared-ui/localization-context";

import { PackageWizardState } from "stores/wizard/state";
import { MultiCityFlightLegProps } from "components/flexComponents/WizardFlightPWA/typings";
import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { locKeys } from "components/flexComponents/WizardFlightPWA/components/l10n";

export const MultiCityFlightLeg = observer((props: MultiCityFlightLegProps) => {
  const defaultMainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12 };
  const defaultLocationColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 9 };
  const defaultDatesColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 3 };
  const defaultRemoveLegColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 11 };
  const defaultCloseRemoveLegColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 1 };

  const removeALeg = (index: number) => {
    props.lobState.removeALeg(index);
  };

  const mainGridColumns = props.mainGridColSpan || defaultMainGridColumns;
  const locationColSpan = props.locationColSpan || defaultLocationColSpan;
  const datesColSpan = props.datesColSpan || defaultDatesColSpan;
  const removeLegColSpan = props.removeLegColSpan || defaultRemoveLegColSpan;
  const closeRemoveLegColSpan = props.closeRemoveLegColSpan || defaultCloseRemoveLegColSpan;
  const minNumberOfLegs = props.lobState.config.multiCityFlight.minNumberOfLegs;

  const { formatText } = useLocalization();

  const isPackagePath = props.lobState instanceof PackageWizardState;

  return (
    <UitkLayoutGridItem colSpan="all">
      <div>
        <fieldset>
          <legend className="is-visually-hidden">{formatText(locKeys.multiCityFlightLegLabel, 1)}</legend>
          <UitkSpacing margin={{ blockstart: "three" }}>
            <UitkLayoutGrid key="multi-city-leg-1" columns={mainGridColumns} space="three">
              <UitkLayoutGridItem colSpan="all">
                <div>
                  <UitkSpacing margin={{ small: { blockstart: "six" }, medium: "unset" }}>
                    <UitkText size={300} aria-hidden={true} weight="bold">
                      {formatText(locKeys.multiCityFlightLegLabel, 1)}
                    </UitkText>
                  </UitkSpacing>
                </div>
              </UitkLayoutGridItem>
              <Location
                setInputsRefs={props.setInputsRefs}
                originField={true}
                destinationField={true}
                lobState={props.lobState}
                showSwapLocationsControl={props.showSwapLocationsControl}
                colSpan={locationColSpan}
              />
              <Dates
                singleDate={true}
                lobState={props.lobState}
                colSpan={datesColSpan}
                setInputsRefs={props.setInputsRefs}
              />
            </UitkLayoutGrid>
          </UitkSpacing>
        </fieldset>
        {props.lobState.listOfAdditionalLegs.map((leg, index) => (
          <fieldset key={`multi-city-leg-${index + 2}`} className="s-t-padding-zero all-t-padding-three">
            <legend className="is-visually-hidden">{formatText(locKeys.multiCityFlightLegLabel, index + 2)}</legend>
            <UitkLayoutGrid
              columns={mainGridColumns}
              space="three"
              className={index + 1 > 0 ? "all-t-margin-three" : ""}
            >
              <UitkLayoutGridItem colSpan={removeLegColSpan}>
                <div>
                  <UitkSpacing margin={{ small: { blockstart: "six" }, medium: "unset" }}>
                    <UitkText size={300} aria-hidden={true} weight="bold">
                      {formatText(locKeys.multiCityFlightLegLabel, index + 2)}
                    </UitkText>
                  </UitkSpacing>
                </div>
              </UitkLayoutGridItem>
              {index + 2 > minNumberOfLegs && (
                <UitkLayoutGridItem colSpan={closeRemoveLegColSpan}>
                  <div>
                    <div className="uitk-flex uitk-flex-justify-content-flex-end s-t-margin-four">
                      <UitkTertiaryButton onClick={removeALeg.bind(this, index)} id="removeFlightLeg">
                        {formatText(locKeys.multiCityFlightRemoveLabel)}
                      </UitkTertiaryButton>
                    </div>
                  </div>
                </UitkLayoutGridItem>
              )}
              <Location
                originField={true}
                destinationField={true}
                lobState={props.lobState}
                multiLegIndex={index}
                showSwapLocationsControl={props.showSwapLocationsControl}
                setInputsRefs={props.setInputsRefs}
                colSpan={locationColSpan}
              />
              <Dates singleDate={true} lobState={props.lobState} multiLegIndex={index} colSpan={datesColSpan} />
              <input
                type="hidden"
                name={isPackagePath ? `legs[${index + 1}]` : `leg${index + 2}`} // input name pattern for flight: leg1,leg2,leg... and for package: legs[0],legs[1],legs[...]
                value={props.lobState.multiCityFlightLegURL[index] || ""} // to maintain input as controlled component before value is defined
                data-testid={`multicity-url`}
              />
            </UitkLayoutGrid>
          </fieldset>
        ))}
      </div>
    </UitkLayoutGridItem>
  );
});
